import { useEffect, useState } from "react";

/** `setTimeout` saturates past 2^31-1 ms and would fire immediately. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * An ISO "now" that re-renders **once**, when `deadlineMs` is reached, instead
 * of ticking. Callers that only need to notice a single instant (a banner that
 * unlocks 70 minutes after the last turn) pay one render at that instant rather
 * than one per minute for as long as the thread stays open.
 *
 * Returns the mount-time instant while the deadline is still ahead, and a fresh
 * one from the moment it passes. Pass `null` when there is no instant to wait
 * for; the value then stays put and no timer is armed.
 */
export function useNowAfter(deadlineMs: number | null): string {
  const [now, setNow] = useState(() => new Date().toISOString());

  useEffect(() => {
    if (deadlineMs === null) return;
    const remaining = deadlineMs - Date.now();
    if (remaining <= 0) return;
    // Depending on `now` re-arms after a clamped wait, so a deadline further
    // out than the timer can express still resolves instead of being dropped.
    const timerId = window.setTimeout(
      () => setNow(new Date().toISOString()),
      Math.min(remaining, MAX_TIMEOUT_MS),
    );
    return () => window.clearTimeout(timerId);
  }, [deadlineMs, now]);

  return now;
}
